import { useEffect, useState } from 'react';
import { Col, Modal, Row, Tabs } from 'antd';
import { CustomButton, CustomTable, FormInput } from '../../../../components';
import { TechnicalConceptAPI } from '../../api/technicalConceptAPI';
import { Evaluation } from '../../types/evaluations-types';

interface IModal {
  setIsModalAddItem: (value: boolean) => void;
  isModalAddItem: boolean;
  formik?: any;
  onAddItem: (item: any) => void;
  addedAssets: Asset[];
  selectedItem: Asset | null;
}

export interface Asset {
  id: number;
  ticket?: string;
  description: string;
  plate_code?: string;
  good_status?: any;
  serial_number?: string;
  cost_center?: { id: number; code: number; name: string };
  responsible?: { id: number; name: string; last_name: string, document: string };
  asset_group?: { id: number; name: string, code: string };
  asset_class?: { id: number; name: string, code: string };
  item?: { id: number; name: string, code: string };
  status?: string
  asset_class_id: number;
  depreciated_useful_life?: number;
  remaining_useful_life_days?: number;
  assets?: Asset[];
  assets_count?: number;
  delivery_deadline?: string;
  user?: string;
  user_role?: string;
  user_email?: string;
  user_id?: number;
  user_document?: string;
  vehicle?: {
    license_plate: string;
    engine_number: string;
    chassis_number: string;
    mechanical_inspection_date: string;
    soat_date: string;
    soat_expiration_date?: string;
    insurance_type: string;
    issue_date: string;
    doors: number;
    armor_level: number;
    fuel_type: string;
    observations: string;
  };
  evaluations?: Evaluation[];
  asset_items?: string;
  technical_concept_typification?: string;
}

const ModalAddItemActivo = ({ setIsModalAddItem, isModalAddItem, formik, selectedItem }: IModal) => {
  const [activeTab, setActiveTab] = useState("1");
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const SIN_REGISTRO = 'Sin registro'

  const costCenterDisplay = selectedItem?.cost_center
    ? `${selectedItem.cost_center.code ?? ''} - ${selectedItem.cost_center.name ?? ''}`
    : SIN_REGISTRO;

  const populateFormikWithAsset = (asset: Asset) => {
    formik.setFieldValue('id', asset.id ?? '');
    formik.setFieldValue('plate_code', asset.plate_code ?? SIN_REGISTRO);
    formik.setFieldValue(
      'responsible',
      asset.responsible
        ? `${asset.responsible.document ?? ''} - ${asset.responsible.name ?? ''} ${asset.responsible.last_name ?? ''}`.trim()
        : SIN_REGISTRO
    );
    formik.setFieldValue(
      'asset_group',
      asset.asset_group ? `${asset.asset_group.code ?? ''} - ${asset.asset_group.name ?? ''}`.trim() : SIN_REGISTRO
    );
    formik.setFieldValue(
      'asset_class',
      asset.asset_class ? `${asset.asset_class.code ?? ''} - ${asset.asset_class.name ?? ''}`.trim() : SIN_REGISTRO
    );
    formik.setFieldValue(
      'item',
      asset.item ? `${asset.item.code ?? ''} - ${asset.item.name ?? ''}`.trim() : SIN_REGISTRO
    );
    formik.setFieldValue('vehicle', asset.vehicle || null);
    formik.setFieldValue('description', asset.description ?? SIN_REGISTRO);
    formik.setFieldValue('serial_number', asset.serial_number ?? SIN_REGISTRO);
    formik.setFieldValue('good_status', asset.good_status?.status ?? SIN_REGISTRO);
    formik.setFieldValue('depreciated_useful_life', asset.depreciated_useful_life ?? 0);
    formik.setFieldValue('cost_center', costCenterDisplay ?? SIN_REGISTRO);
    formik.setFieldValue('remaining_useful_life_days', asset.remaining_useful_life_days ?? 0);
  };

  useEffect(() => {
    if (selectedItem) {
      setSelectedId(selectedItem.id.toString());
      setSelectedAsset(selectedItem);
      populateFormikWithAsset(selectedItem);
    } else {
      setSelectedId(null);
    }
  }, [selectedItem]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const storedToken = localStorage.getItem('auth');
        const parsedToken = storedToken ? JSON.parse(storedToken) : null;

        if (!parsedToken || !parsedToken.user) {
          console.error('No se encontró el usuario en el token.');
          return;
        }

        const data = await TechnicalConceptAPI.fetchParameters(
          ['assets_data'],
          { responsible_id: parsedToken.user.id }
        );

        if (data && Array.isArray(data.assets_data)) {
          setAssets(
            data.assets_data.map((asset: Asset) => ({
              ...asset,
            }))
          );
        }
      } catch (error) {
        console.error('Error fetching assets:', error);
      }
    };
    fetchData();

  }, []);

  const columnsAdditions = [
    {
      title: 'Ítem',
      dataIndex: 'item_id',
      key: 'item_id',
      render: (_: any, record: Asset) => record.item?.id || 'N/A',
    },
    {
      title: 'Código/placa',
      dataIndex: 'plate_code',
      key: 'plate_code',
      render: (_: any, record: Asset) => record.plate_code || 'N/A',
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      render: (_: any, record: Asset) => record.description || 'N/A',
    },
    {
      title: 'Clase de activos',
      dataIndex: 'asset_class',
      key: 'asset_class',
      render: (_: any, record: Asset) =>
        record.asset_class
          ? `${record.asset_class.code} - ${record.asset_class.name}`
          : 'N/A',
    },
    {
      title: 'Artículo',
      dataIndex: 'item_name',
      key: 'item_name',
      render: (_: any, record: Asset) =>
        record.item
          ? `${record.item.code} - ${record.item.name}`
          : 'N/A',
    },
    {
      title: 'Estado del bien',
      dataIndex: 'good_status',
      key: 'good_status',
      render: (_: any, record: Asset) => record.good_status?.status || 'N/A',
    },
  ];

  return (
    <Modal
      width={800}
      title="Ver activo"
      open={isModalAddItem}
      onCancel={() => setIsModalAddItem(false)}
      footer={[
        selectedAsset?.vehicle && activeTab === "1" && (
          <CustomButton
            key="next"
            label="Continuar"
            onClick={() => setActiveTab("2")}
            buttonType="primary"
          />
        ),
        activeTab === "2" && (
          <CustomButton
            key="back"
            label="Atrás"
            onClick={() => setActiveTab("1")}
            buttonType="secondary"
          />
        ),
        <CustomButton
          key="finish"
          label="Finalizar"
          onClick={() => setIsModalAddItem(false)}
        />
      ]}
    >
      <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)}>
        <Tabs.TabPane tab="Datos del activo" key="1">
          <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FormInput label="Código/Placa" name="plate_code" value={formik.values.plate_code} disabled />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FormInput label="Descripción" name="description" value={formik.values.description} disabled />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FormInput label="Número de serie" name="asset_class" value={formik.values.serial_number} disabled />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FormInput
                label="Vida útil del activo"
                name="useful_life_asset"
                value={`${Number(formik.values.remaining_useful_life_days ?? 0) +
                  Number(formik.values.depreciated_useful_life ?? 0)
                  }`}
                disabled
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FormInput label="Grupo de activos" name="asset_group" value={formik.values.asset_group} disabled />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FormInput label="Clase de activo" name="asset_class" value={formik.values.asset_class} disabled />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FormInput label="Artículo" name="item" value={formik.values.item} disabled />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FormInput label="Vida útil depreciada" name="depreciated_useful_life" value={formik.values.depreciated_useful_life} disabled />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FormInput label="Vida útil por depreciar" name="remaining_useful_life_days" value={formik.values.remaining_useful_life_days} disabled />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FormInput label="Responsable" name="responsible" value={formik.values.responsible} disabled />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FormInput label="Centro de costos" name="cost_center" value={costCenterDisplay} disabled />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FormInput label="Estado del bien" name="good_status" value={formik.values.good_status} disabled />
            </Col>
          </Row>

          <Row>
            <Col xs={24}>
              <h4>Adiciones</h4>
              <CustomTable
                columns={columnsAdditions}
                dataSource={selectedAsset && Array.isArray(selectedAsset.assets)
                  ? selectedAsset.assets
                    .filter(a => a && typeof a === 'object')
                    .map((a, index) => ({ ...a, key: index }))
                  : []}
                pagination={{ pageSize: 5 }}
                scroll={{ x: 'max-content' }} />
            </Col>
          </Row>
        </Tabs.TabPane>

        {selectedAsset?.vehicle && (
          <Tabs.TabPane tab="Vehículo" key="2">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Placa" name="license_plate" value={selectedAsset.vehicle.license_plate ?? SIN_REGISTRO} disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Número de motor" name="engine_number" value={selectedAsset.vehicle.engine_number ?? SIN_REGISTRO} disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Número de chasis" name="chassis_number" value={selectedAsset.vehicle.chassis_number ?? SIN_REGISTRO} disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Fecha tecnomecánica" name="mechanical_inspection_date" value={selectedAsset.vehicle.mechanical_inspection_date ?? SIN_REGISTRO} disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Tipo de seguro" name="" value='SOAT' disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Fecha de expedición" name="soat_date" value={selectedAsset.vehicle.soat_date ?? SIN_REGISTRO} disabled />
              </Col>
              {/* <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Fecha de vencimiento" name="soat_date" value={selectedAsset.vehicle.soat_expiration_date ?? SIN_REGISTRO} disabled />
              </Col> */}
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Número de puertas" name="doors" value={selectedAsset.vehicle.doors ?? SIN_REGISTRO} disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Nivel de blindaje" name="armor_level" value={selectedAsset.vehicle.armor_level ?? SIN_REGISTRO} disabled />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormInput label="Tipo de combustible" name="fuel_type" value={selectedAsset.vehicle.fuel_type ?? SIN_REGISTRO} disabled />
              </Col>
            </Row>
          </Tabs.TabPane>
        )}
      </Tabs>
    </Modal>
  );
};

export default ModalAddItemActivo;